import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface PlatformUser {
  userId: string;
  email: string;
  phone: string;
  name: string;
  userType: 'customer' | 'provider';
  profileData?: Record<string, unknown>;
  notificationPreferences?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface UserListResponse {
  users: PlatformUser[];
  count: number;
  lastEvaluatedKey?: Record<string, unknown>;
}

export interface UserBookingsResponse {
  bookings: unknown[];
  count: number;
}

export interface UserPaymentsResponse {
  payments: unknown[];
  count: number;
}

export interface UserReviewsResponse {
  reviews: unknown[];
  count: number;
}

export interface ListUsersParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  userType?: 'customer' | 'provider';
  email?: string;
}

class UserManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async listUsers(params?: ListUsersParams): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.userType) queryParams.append('userType', params.userType);
    if (params?.email) queryParams.append('email', params.email);

    const response = await this.api.get<{success: boolean; data: UserListResponse}>(
      `/admin/users?${queryParams.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to list users');
    }

    return response.data.data;
  }

  async getUser(userId: string): Promise<PlatformUser> {
    const response = await this.api.get<{success: boolean; data: {user: PlatformUser}}>(
      `/admin/users/${userId}`
    );

    if (!response.data.success || !response.data.data?.user) {
      throw new Error('Failed to get user');
    }

    return response.data.data.user;
  }

  async updateUser(userId: string, updates: Partial<PlatformUser>): Promise<PlatformUser> {
    const response = await this.api.put<{success: boolean; data: {user: PlatformUser}}>(
      `/admin/users/${userId}`,
      updates
    );

    if (!response.data.success || !response.data.data?.user) {
      throw new Error('Failed to update user');
    }

    return response.data.data.user;
  }

  async deleteUser(userId: string): Promise<void> {
    const response = await this.api.delete<{success: boolean; data: {message: string}}>(
      `/admin/users/${userId}`
    );

    if (!response.data.success) {
      throw new Error('Failed to delete user');
    }
  }

  async getUserBookings(userId: string): Promise<UserBookingsResponse> {
    const response = await this.api.get<{success: boolean; data: UserBookingsResponse}>(
      `/admin/users/${userId}/bookings`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get user bookings');
    }

    return response.data.data;
  }

  async getUserPayments(userId: string): Promise<UserPaymentsResponse> {
    const response = await this.api.get<{success: boolean; data: UserPaymentsResponse}>(
      `/admin/users/${userId}/payments`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get user payments');
    }

    return response.data.data;
  }

  async getUserReviews(userId: string): Promise<UserReviewsResponse> {
    const response = await this.api.get<{success: boolean; data: UserReviewsResponse}>(
      `/admin/users/${userId}/reviews`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error('Failed to get user reviews');
    }

    return response.data.data;
  }
}

export const userManagementService = new UserManagementService();

